import { inject, Service, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { IWebConfig } from 'src/app/shared/model/IWebConfig';
import { MerchantDetails } from 'src/app/shared/model/merchant-details.model';
import { OrgId } from 'src/app/shared/model/org-id.enum';
import { PortalId } from 'src/app/shared/model/portal-id.enum';
import { Role } from 'src/app/shared/model/user-role.enum';
import { User } from 'src/app/shared/model/user.model';
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { WebConfigService } from './web-config/web-config.service';
import { FeatureToggleService } from './feature-toggle/feature-toggle.service';
import { Feature } from '../model/features.enum';

const BREAKPOINT = 1024;

// Menu
export interface Menu {
  headTitle1?: string;
  headTitle2?: string;
  path?: string;
  title?: string;
  icon?: string;
  type?: string;
  badgeType?: string;
  badgeValue?: string;
  active?: boolean;
  bookmark?: boolean;
  children?: Menu[];
}

@Service()
export class NavService implements OnDestroy {
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(window.innerWidth);
  #featureToggle = inject(FeatureToggleService);
  // Search Box
  // Demo mode
  public demoMode = true;
  // Collapse Sidebar
  public collapseSidebar: boolean = window.innerWidth <= BREAKPOINT;
  // For Horizontal Layout Mobile
  public horizontal: boolean = window.innerWidth > BREAKPOINT;
  // Profile complete to show badge on the menu item
  public profileComplete = false;
  public helpCentre = {
    title: 'Demo Centre',
    type: 'sub',
    icon: 'help-circle',
    children: [
      { title: 'Scan to Tap', path: '/scan-to-pay', type: 'link' },
      { title: 'Tap on Own', path: '/tap-on-own', type: 'link' },
      { title: 'Push to Pay', path: '/push-to-pay', type: 'link' },
    ],
  };

  public helpCentreDemoMode = {
    title: 'Demo Centre',
    type: 'sub',
    icon: 'help-circle',
    children: [
      { title: 'Scan to Tap', path: '/scan-to-pay', type: 'link' },
      { title: 'Push to Pay', path: '/push-to-pay', type: 'link' },
      { title: 'Upgrade', path: '/upgrade', type: 'link' },
    ],
  };

  // (internal only)
  public buildApk = {
    title: 'Build APK',
    path: '/build-apk',
    type: 'link',
    icon: 'smartphone',
  };

  public users = {
    title: 'Users',
    path: '/users',
    type: 'link',
    icon: 'users',
  };

  public disabledUsers = {
    title: 'Users',
    type: 'disabled-link',
    icon: 'users',
  };

  public statementsMenu = {
    title: 'Statements',
    path: '/statements',
    type: 'link',
    icon: 'file',
  };

  public demoStatementsMenu = {
    title: 'Statements',
    type: 'demo-link',
    icon: 'file',
  };

  public merchantPayoutRequestsMenu = {
    title: 'Payout Requests',
    path: '/payouts',
    type: 'link',
    icon: 'dollar-sign',
  };

  public profileMenuMerchant = {
    title: 'Profile',
    path: '/profile/merchant',
    type: 'link',
    icon: 'user',
    badgeType: '',
    badgeValue: '',
  };

  public menuItems = new BehaviorSubject<Menu[]>([]);
  public merchantMenuItems = new BehaviorSubject<Menu[]>([]);
  public supportMenuItems = new BehaviorSubject<Menu[]>([]);
  public developerMenuItems = new BehaviorSubject<Menu[]>([]);

  private webconfig: IWebConfig = inject(WebConfigService);
  private router = inject(Router);
  private api = inject(ApiService);
  private authService = inject(AuthService);
  private logger = inject(NGXLogger);
  private unsubscriber: Subject<void> = new Subject();
  private menusGenerated = false;
  private menuArraysGenerated = false;
  private MENU_MERCHANT: Menu[];
  private MENU_MERCHANT_DEMO_MODE: Menu[];
  private MENU_SUPPORT: Menu[];
  private MENU_DEVELOPER: Menu[];

  constructor() {
    this.setScreenWidth(window.innerWidth);
    this.setupResizeListener();
    this.setupRouteChangeListener();
    this.init();
  }

  init() {
    try {
      if (!this.menusGenerated) {
        this.generateMenus();
      }

      this.authService.user.subscribe((user: User) => {
        if (user !== null) this.fetchProfileCompleteness(user);
      });

      this.authService.merchant.subscribe((merchant: MerchantDetails) => {
        if (merchant !== null) {
          if (merchant.demoMode) {
            this.merchantMenuItems.next(this.MENU_MERCHANT_DEMO_MODE);
          } else {
            this.merchantMenuItems.next(this.MENU_MERCHANT);
          }
        }
      });

      if (!this.menuArraysGenerated) {
        this.populateMenuArrays();
      }

      switch (this.webconfig.portalId) {
        case 'merchant':
          this.menuItems = this.merchantMenuItems;
          break;

        case 'support':
          this.menuItems = this.supportMenuItems;
          break;

        case 'developer':
          this.menuItems = this.developerMenuItems;
          break;

        default:
          this.menuItems = this.merchantMenuItems;
          break;
      }
    } catch (error) {
      this.logger.error('An error occurred while initializing the navigation service:', error);
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  closeSidebar() {
    this.collapseSidebar = window.innerWidth <= BREAKPOINT;
  }

  private setupResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(1000), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < BREAKPOINT) {
          this.collapseSidebar = true;
        }
      });
  }

  private setupRouteChangeListener(): void {
    if (window.innerWidth < BREAKPOINT) {
      this.router.events.subscribe((event) => {
        this.collapseSidebar = true;
      });
    }
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  private fetchProfileCompleteness(user: User) {
    if (user.role === Role.Merchant) {
      this.api
        .getMerchantCompleteness()
        .then((completeness) => {
          if (completeness.businessProfile.complete) {
            this.profileComplete = true;
          }

          if (completeness.businessAddress.complete) {
            this.profileComplete = true;
          }

          if (completeness.businessContact.complete) {
            this.profileComplete = true;
          }

          if (completeness.bankAccount.complete) {
            this.profileComplete = true;
          }

          if (completeness.externalMerchantSystem?.complete) {
            this.profileComplete = true;
          }

          if (!this.profileComplete) {
            this.profileMenuMerchant.badgeType = 'danger';
            this.profileMenuMerchant.badgeValue = '1';
          } else {
            this.profileMenuMerchant.badgeType = '';
            this.profileMenuMerchant.badgeValue = '';
          }
        })
        .catch((e) => {
          this.logger.error(e);
        });
    }
  }

  // eslint-disable-next-line max-lines-per-function
  private generateMenus() {
    this.MENU_MERCHANT = [
      {
        title: 'Dashboard',
        path: '/dashboard',
        type: 'link',
        icon: 'home',
      },
      this.profileMenuMerchant,
      {
        title: 'Back Office',
        path: '/backoffice',
        type: 'link',
        icon: 'archive',
      },
      this.webconfig.showStatements ? this.statementsMenu : {},
      this.webconfig.showPayoutRequests ? this.merchantPayoutRequestsMenu : {},
      this.webconfig.portalId === PortalId.Merchant && this.webconfig.orgId === OrgId.Go ? this.helpCentre : {},
    ];

    this.MENU_MERCHANT_DEMO_MODE = [
      {
        title: 'Dashboard',
        path: '/dashboard',
        type: 'link',
        icon: 'home',
      },
      this.profileMenuMerchant,
      {
        title: 'Transactions',
        path: '/transactions',
        type: 'link',
        icon: 'credit-card',
      },
      this.webconfig.showStatements &&
      this.webconfig.portalId === PortalId.Merchant &&
      this.webconfig.orgId === OrgId.Go
        ? this.demoStatementsMenu
        : {},
      this.webconfig.showPayoutRequests ? this.merchantPayoutRequestsMenu : {},
      {
        title: 'My Devices',
        path: '/devices',
        type: 'link',
        icon: 'tablet',
      },
      {
        title: 'Users',
        path: '/users',
        type: 'link',
        icon: 'users',
      },
      this.webconfig.portalId === PortalId.Merchant && this.webconfig.orgId === OrgId.Go ? this.helpCentreDemoMode : {},
    ];

    this.MENU_SUPPORT = [
      {
        title: 'Dashboard',
        path: '/dashboard',
        type: 'link',
        icon: 'home',
      },
      {
        title: 'Back Office',
        path: '/backoffice',
        type: 'link',
        icon: 'archive',
      },
    ];

    this.MENU_DEVELOPER = [
      {
        title: 'Dashboard',
        path: '/get-started',
        type: 'link',
        icon: 'home',
      },
      ...(this.#featureToggle
        .when(Feature.WebBackend)
        .isOff(() => [
          {
            title: 'Back Office',
            path: '/backoffice',
            type: 'link',
            icon: 'archive',
          },
        ])
        .isOn(() => [
          {
            title: 'Back Office',
            type: 'sub',
            icon: 'archive',
            children: [
              { title: 'Halo.Go (App-to-App)', path: '/backoffice/halo-go-app', type: 'link' },
              { title: 'Halo.Go (SDK)', path: '/backoffice/halo-go-sdk', type: 'link' },
              { title: 'Your Sandbox (Coming soon!)', type: 'disabled-link' },
            ],
          },
          {
            title: 'Settings',
            type: 'sub',
            icon: 'settings',
            children: [{ title: 'Team Management', path: '/team-management', type: 'link' }],
          },
        ])() as Menu[]),
    ];

    this.menusGenerated = true;
  }

  private populateMenuArrays() {
    this.merchantMenuItems = new BehaviorSubject<Menu[]>(this.MENU_MERCHANT);
    this.supportMenuItems = new BehaviorSubject<Menu[]>(this.MENU_SUPPORT);
    this.developerMenuItems = new BehaviorSubject<Menu[]>(this.MENU_DEVELOPER);

    this.menuArraysGenerated = true;
  }
}
